import React, { useCallback, useEffect, forwardRef, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import moment from 'moment'
import toast from 'react-hot-toast'
import DatePicker from 'react-datepicker'
import { hijriMonthsFull, day } from '@core/layouts/utils'
import Drawer from '@mui/material/Drawer'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import TextField from '@mui/material/TextField'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import { styled } from '@mui/material/styles'
import MuiFormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import _ from 'lodash'
import FormHeader from 'views/components/formheader'
import FormButtons from 'views/components/formbuttons'
import APICall from 'network/APICall'
import EndPoints from 'network/Endpoints'
import DatePickerWrapper from '@core/styles/react-datepicker'
import { isSpaceValidateTextField } from '@core/utils/Const'

const PickersComponent = forwardRef(({ ...props }, ref) => {
  const { label } = props

  return <TextField inputRef={ref} {...props} label={label || ''} />
})

const FormControlLabel = styled(MuiFormControlLabel)(({ theme }) => ({
  '& .MuiFormControlLabel-label': {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary
  }
}))

const AddCalendarSetting = props => {
  const { open, toggle, heading, onSubmitCalendar, type, updateData } = props

  const [updateDate, selectUpdateDate] = useState(updateData?.date)

  const defaultValues = {
    date: '',
    month: '',
    day: '',
    year: '',
    specialDays: '',
    red: '',
    back: '',
    exact_english_date: '',
    exact_hijri_date: ''
  }

  const {
    setValue,
    reset,
    control,
    clearErrors,
    handleSubmit,
    formState: { errors }
  } = useForm({ defaultValues })

  const resetToEmptyValues = useCallback(() => {
    setValue('date', '')
    setValue('month', '')
    setValue('day', '')
    setValue('year', '')
    setValue('specialDays', '')
    setValue('red', false)
    setValue('black', false)
    setValue('exact_english_date', false)
    setValue('exact_hijri_date', false)
  }, [setValue])

  const handleClose = () => {
    if (type === 'add') {
      reset(defaultValues)
    }
    clearErrors()
    toggle()
  }

  const resetToStoredValues = useCallback(() => {
    const split = _.split(updateData.full_date, ' ')
    const month = split.slice(1, -1).join(' ').replace(',', '')
    const days = split[0]
    const years = _.last(split)

    if (type === 'edit') {
      const event = {
        date: updateData?.date,
        day: days,
        month: month,
        year: years,
        specialDays: updateData?.special_day_title,
        color: updateData?.color,
        exact_english_date: updateData?.is_exact_english_date,
        exact_hijri_date: updateData?.is_exact_hijri_date
      }
      selectUpdateDate(updateData?.date)
      setValue('date', event.date || '')
      setValue('day', event.day || '')
      setValue('month', event.month || '')
      setValue('year', event.year || '')
      setValue('specialDays', event.specialDays || '')
      setValue('red', event.color === 'red')
      setValue('black', event.color === 'black')
      setValue('exact_english_date', event.exact_english_date || false)
      setValue('exact_hijri_date', event.exact_hijri_date || false)
    }
  }, [setValue, type, updateData])

  useEffect(() => {
    if (type === 'edit') {
      resetToStoredValues()
    } else {
      resetToEmptyValues()
    }
  }, [open, resetToStoredValues, resetToEmptyValues, type])

  const onHandleDate = date => {
    setValue('date', date)
    selectUpdateDate(date)
  }

  const onSubmit = data => {
    const format = moment(data.date).format('MM/DD/yyyy')
    const selectBlack = data.black ? 'black' : ''
    const payload = {
      date: format,
      hijriFullDate: `${data.day} ${data.month}, ${data.year}`,
      special_day_title: data.specialDays,
      color: data.red ? 'red' : selectBlack,
      exact_english_date: data.exact_english_date,
      exact_hijri_date: data.exact_hijri_date
    }

    if (type === 'add') {
      APICall('post', payload, EndPoints.addCalendarDate)
        .then(response => {
          const { isError, message } = response.data
          if (!isError) {
            toast.success(message)
            onSubmitCalendar()
            handleClose()
          } else {
            toast.error(message)
          }
        })
        .catch(error => {
          return error
        })
    } else {
      const payloads = {
        id: updateData.id,
        ...payload
      }
      APICall('post', payloads, EndPoints.updateCalendarDate)
        .then(response => {
          const { isError, message } = response.data
          if (!isError) {
            toast.success(message)
            onSubmitCalendar()
            handleClose()
          } else {
            toast.error(message)
          }
        })
        .catch(error => {
          return error
        })
    }
  }

  return (
    <Drawer
      open={open}
      anchor='right'
      variant='temporary'
      onClose={handleClose}
      ModalProps={{ keepMounted: true }}
      sx={{ '& .MuiDrawer-paper': { width: { xs: 350, sm: 430 } } }}
    >
      <FormHeader heading={heading} onClick={handleClose} />

      <Box sx={{ p: 5 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <DatePickerWrapper>
                <FormControl fullWidth>
                  <Box sx={{ display: 'flex', width: '100%' }}>
                    <Controller
                      name='date'
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { value } }) => (
                        <>
                          {type === 'edit' ? (
                            <DatePicker
                              selected={
                                new Date(
                                  updateDate === undefined
                                    ? updateData?.date
                                    : updateDate
                                )
                              }
                              onChange={onHandleDate}
                              error={Boolean(errors.date)}
                              customInput={<PickersComponent label='Date' />}
                              autoComplete={'off'}
                              dateFormat='dd/MM/yyyy'
                            />
                          ) : (
                            <DatePicker
                              selected={value}
                              dateFormat='dd/MM/yyyy'
                              onChange={onHandleDate}
                              error={Boolean(errors.date)}
                              customInput={<PickersComponent label='Date' />}
                              autoComplete={'off'}
                            />
                          )}
                        </>
                      )}
                    />
                  </Box>
                </FormControl>
                {errors.date && (
                  <FormHelperText
                    sx={{ color: 'error.main' }}
                    id='validation-basic-select'
                  >
                    This field is required
                  </FormHelperText>
                )}
              </DatePickerWrapper>
            </Grid>

            <Grid item xs={4}>
              <FormControl fullWidth>
                <Controller
                  name='day'
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <>
                      <InputLabel
                        id='validation-basic-select'
                        error={Boolean(errors.month)}
                        htmlFor='validation-basic-select'
                      >
                        Day
                      </InputLabel>
                      <Select
                        label={'Day'}
                        onChange={onChange}
                        error={Boolean(errors.day)}
                        labelId='validation-basic-select'
                        aria-describedby='validation-basic-select'
                        value={value}
                      >
                        {day.map((item, index) => (
                          <MenuItem value={item} key={index}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  )}
                />
                {errors.day && (
                  <FormHelperText
                    sx={{ color: 'error.main' }}
                    id='validation-basic-select'
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <Controller
                  name='month'
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <>
                      <InputLabel
                        id='validation-basic-select'
                        error={Boolean(errors.month)}
                        htmlFor='validation-basic-select'
                      >
                        Month
                      </InputLabel>
                      <Select
                        label={'Month'}
                        onChange={onChange}
                        error={Boolean(errors.month)}
                        labelId='validation-basic-select'
                        aria-describedby='validation-basic-select'
                        value={value}
                      >
                        {hijriMonthsFull.map((item, index) => (
                          <MenuItem value={item.name} key={index}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  )}
                />
                {errors.month && (
                  <FormHelperText
                    sx={{ color: 'error.main' }}
                    id='validation-basic-select'
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <Controller
                  name='year'
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type='text'
                      label='Year'
                      error={Boolean(errors.year)}
                      placeholder='Year'
                      aria-describedby='validation-basic-first-name'
                    />
                  )}
                />
                {errors.year && (
                  <FormHelperText
                    sx={{ color: 'error.main' }}
                    id='validation-basic-select'
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name='specialDays'
                control={control}
                rules={{
                  required: false
                }}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    fullWidth
                    type='text'
                    label='Special Day'
                    placeholder='Enter special day'
                  />
                )}
              />
              {errors.specialDays && (
                <FormHelperText
                  sx={{ color: 'error.main' }}
                  id='validation-basic-select'
                >
                 This field is required
                </FormHelperText>
              )}
            </Grid>

            <Grid
              item
              xs={12}
              sx={{
                paddingLeft: '1rem',
                paddingTop: '0.4rem',
                alignItems: 'center',
                justifyContent: 'space-between',
                display: 'flex'
              }}
            >
              <Box
                sx={{
                  flex: 2,
                  justifyContent: 'flex-start',
                  flexDirection: 'column'
                }}
              >
                <FormControl>
                  <Controller
                    name='red'
                    control={control}
                    rules={{ required: false }}
                    render={({ field: { value } }) => {
                      return (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={value}
                              onChange={e => {
                                setValue('red', e.target.checked)
                                setValue('black', false)
                              }}
                            />
                          }
                          label={
                            <Typography variant='body1' component='span'>
                              Red Circle
                            </Typography>
                          }
                        />
                      )
                    }}
                  />
                </FormControl>
              </Box>

              <Box
                sx={{
                  flex: 2,
                  justifyContent: 'flex-end',
                  flexDirection: 'column'
                }}
              >
                <FormControl>
                  <Controller
                    name='black'
                    control={control}
                    rules={{ required: false }}
                    render={({ field: { value } }) => {
                      return (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={value}
                              onChange={e => {
                                setValue('black', e.target.checked)
                                setValue('red', false)
                              }}
                            />
                          }
                          label={
                            <Typography variant='body1' component='span'>
                              Black Circle
                            </Typography>
                          }
                        />
                      )
                    }}
                  />
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} sx={{ paddingLeft: '1rem' }}>
              <FormControl fullWidth>
                <Controller
                  name='exact_english_date'
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <FormControlLabel
                        control={
                          <Checkbox checked={value} onChange={onChange} />
                        }
                        label={
                          <Typography variant='body1' component='span'>
                            Exact English Date (for non-recurring event)
                          </Typography>
                        }
                      />
                    )
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ paddingLeft: '1rem' }}>
              <FormControl fullWidth>
                <Controller
                  name='exact_hijri_date'
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <FormControlLabel
                        control={
                          <Checkbox checked={value} onChange={onChange} />
                        }
                        label={
                          <Typography variant='body1' component='span'>
                            Exact Hijri Date (for non-recurring event)
                          </Typography>
                        }
                      />
                    )
                  }}
                />
              </FormControl>
            </Grid>
            <FormButtons type={type} resetFunction={resetToEmptyValues} />
          </Grid>
        </form>
      </Box>
    </Drawer>
  )
}

export default AddCalendarSetting
